import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { cn } from '../utils/cn'

// Sample order items
const orderItems = [
  {
    id: 1,
    name: 'Rose Quartz',
    price: 49,
    image: '/images/product-1.png',
    color: 'Rose Pink',
    size: 'M',
    quantity: 1,
  },
  {
    id: 4,
    name: 'Golden Hour',
    price: 59,
    image: '/images/product-1.png',
    color: 'Gold',
    size: 'S',
    quantity: 2,
  },
]

function FormInput({ label, type = 'text', placeholder, required = false, className = '', ...props }) {
  return (
    <div className={cn("flex flex-col", className)}>
      <label className="text-xs uppercase tracking-[0.1em] text-text-primary mb-2">
        {label} {required && <span className="text-accent">*</span>}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        required={required}
        className={cn(
          "h-12 px-4",
          "bg-transparent border border-border",
          "text-sm text-text-primary placeholder:text-text-muted",
          "focus:outline-none focus:border-text-primary",
          "transition-colors duration-200"
        )}
        {...props}
      />
    </div>
  )
}

function FormSelect({ label, options, required = false, className = '', ...props }) {
  return (
    <div className={cn("flex flex-col", className)}>
      <label className="text-xs uppercase tracking-[0.1em] text-text-primary mb-2">
        {label} {required && <span className="text-accent">*</span>}
      </label>
      <select
        required={required}
        className={cn(
          "h-12 px-4",
          "bg-transparent border border-border",
          "text-sm text-text-primary",
          "focus:outline-none focus:border-text-primary",
          "transition-colors duration-200",
          "cursor-pointer appearance-none",
          "bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg%20xmlns%3d%22http%3a%2f%2fwww.w3.org%2f2000%2fsvg%22%20width%3d%2224%22%20height%3d%2224%22%20viewBox%3d%220%200%2024%2024%22%20fill%3d%22none%22%20stroke%3d%22%238A8A8A%22%20stroke-width%3d%221.5%22%20stroke-linecap%3d%22round%22%20stroke-linejoin%3d%22round%22%3e%3cpath%20d%3d%22M6%209l6%206%206-6%22%2f%3e%3c%2fsvg%3e')] bg-no-repeat bg-[right_1rem_center] bg-[length:1rem]"
        )}
        {...props}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function CheckoutStep({ number, title, isActive, isComplete }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors",
          isComplete
            ? "bg-text-primary text-white"
            : isActive
            ? "bg-text-primary text-white"
            : "bg-bg-tertiary text-text-muted"
        )}
      >
        {isComplete ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          number
        )}
      </div>
      <span
        className={cn(
          "text-xs uppercase tracking-wider transition-colors",
          isActive ? "text-text-primary" : "text-text-muted"
        )}
      >
        {title}
      </span>
    </div>
  )
}

function OrderSummaryItem({ item }) {
  return (
    <div className="flex gap-4 py-4 border-b border-border last:border-0">
      <div className="w-16 h-16 bg-bg-tertiary flex-shrink-0">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex-1">
        <h4 className="text-sm font-medium text-text-primary">{item.name}</h4>
        <p className="text-xs text-text-muted mt-1">
          {item.color} / Size {item.size}
        </p>
        <p className="text-xs text-text-muted">Qty: {item.quantity}</p>
      </div>
      <p className="text-sm text-text-primary">${item.price * item.quantity}</p>
    </div>
  )
}

export default function CheckoutPage() {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    country: 'US',
    state: '',
    zipCode: '',
    phone: '',
    shippingMethod: 'standard',
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
  })

  const subtotal = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const shipping = formData.shippingMethod === 'express' ? 15 : 8
  const tax = Math.round(subtotal * 0.08)
  const total = subtotal + shipping + tax

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const countries = [
    { value: 'US', label: 'United States' },
    { value: 'CA', label: 'Canada' },
    { value: 'UK', label: 'United Kingdom' },
    { value: 'AU', label: 'Australia' },
  ]

  const states = [
    { value: '', label: 'Select State' },
    { value: 'CA', label: 'California' },
    { value: 'NY', label: 'New York' },
    { value: 'TX', label: 'Texas' },
    { value: 'FL', label: 'Florida' },
  ]

  return (
    <div className="min-h-screen bg-bg-primary pt-20 lg:pt-24">
      {/* Checkout Header */}
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="font-display font-bold text-text-primary tracking-wide text-lg">
              Nakhoons
            </Link>

            {/* Checkout Steps */}
            <div className="hidden md:flex items-center gap-8">
              <CheckoutStep number={1} title="Information" isActive={currentStep === 1} isComplete={currentStep > 1} />
              <div className="w-12 h-px bg-border" />
              <CheckoutStep number={2} title="Shipping" isActive={currentStep === 2} isComplete={currentStep > 2} />
              <div className="w-12 h-px bg-border" />
              <CheckoutStep number={3} title="Payment" isActive={currentStep === 3} isComplete={false} />
            </div>

            {/* Cart Link */}
            <Link to="/cart" className="text-xs uppercase tracking-wider text-text-muted hover:text-text-primary transition-colors">
              Return to Cart
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left: Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Contact Information */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-serif text-text-primary">Contact Information</h2>
                <p className="text-xs text-text-muted">
                  Already have an account?{' '}
                  <button className="text-text-primary underline hover:no-underline">
                    Log in
                  </button>
                </p>
              </div>

              <FormInput
                label="Email"
                type="email"
                name="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleInputChange}
                required
              />

              <label className="flex items-center gap-3 mt-4 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 border border-border accent-text-primary cursor-pointer"
                />
                <span className="text-sm text-text-secondary group-hover:text-text-primary transition-colors">
                  Email me with news and offers
                </span>
              </label>
            </div>

            {/* Shipping Address */}
            <div className="mb-10">
              <h2 className="text-lg font-serif text-text-primary mb-6">Shipping Address</h2>

              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="First Name"
                  name="firstName"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
                <FormInput
                  label="Last Name"
                  name="lastName"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="mt-4">
                <FormInput
                  label="Address"
                  name="address"
                  placeholder="123 Main Street"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="mt-4">
                <FormInput
                  label="Apartment, suite, etc."
                  name="apartment"
                  placeholder="Apt 4B (optional)"
                  value={formData.apartment}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <FormInput
                  label="City"
                  name="city"
                  placeholder="New York"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                />
                <FormSelect
                  label="Country"
                  name="country"
                  options={countries}
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <FormSelect
                  label="State"
                  name="state"
                  options={states}
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                />
                <FormInput
                  label="ZIP Code"
                  name="zipCode"
                  placeholder="10001"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="mt-4">
                <FormInput
                  label="Phone"
                  type="tel"
                  name="phone"
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Shipping Method */}
            <div className="mb-10">
              <h2 className="text-lg font-serif text-text-primary mb-6">Shipping Method</h2>

              <div className="space-y-3">
                <label
                  className={cn(
                    "flex items-center justify-between p-4 border cursor-pointer transition-all duration-200",
                    formData.shippingMethod === 'standard'
                      ? "border-text-primary bg-bg-secondary"
                      : "border-border hover:border-text-muted"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="shippingMethod"
                      value="standard"
                      checked={formData.shippingMethod === 'standard'}
                      onChange={handleInputChange}
                      className="w-4 h-4 accent-text-primary"
                    />
                    <div>
                      <p className="text-sm text-text-primary">Standard Shipping</p>
                      <p className="text-xs text-text-muted">5-7 business days</p>
                    </div>
                  </div>
                  <span className="text-sm text-text-primary">$8.00</span>
                </label>

                <label
                  className={cn(
                    "flex items-center justify-between p-4 border cursor-pointer transition-all duration-200",
                    formData.shippingMethod === 'express'
                      ? "border-text-primary bg-bg-secondary"
                      : "border-border hover:border-text-muted"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="shippingMethod"
                      value="express"
                      checked={formData.shippingMethod === 'express'}
                      onChange={handleInputChange}
                      className="w-4 h-4 accent-text-primary"
                    />
                    <div>
                      <p className="text-sm text-text-primary">Express Shipping</p>
                      <p className="text-xs text-text-muted">2-3 business days</p>
                    </div>
                  </div>
                  <span className="text-sm text-text-primary">$15.00</span>
                </label>
              </div>
            </div>

            {/* Payment */}
            <div className="mb-10">
              <h2 className="text-lg font-serif text-text-primary mb-6">Payment</h2>
              <p className="text-xs text-text-muted mb-6">All transactions are secure and encrypted.</p>

              <div className="border border-border p-6">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-text-primary">Credit Card</p>
                  <div className="flex gap-2">
                    <div className="w-10 h-6 bg-bg-tertiary rounded flex items-center justify-center text-[10px] text-text-muted">VISA</div>
                    <div className="w-10 h-6 bg-bg-tertiary rounded flex items-center justify-center text-[10px] text-text-muted">MC</div>
                    <div className="w-10 h-6 bg-bg-tertiary rounded flex items-center justify-center text-[10px] text-text-muted">AMEX</div>
                  </div>
                </div>

                <FormInput
                  label="Card Number"
                  name="cardNumber"
                  placeholder="1234 5678 9012 3456"
                  value={formData.cardNumber}
                  onChange={handleInputChange}
                  required
                />

                <div className="mt-4">
                  <FormInput
                    label="Name on Card"
                    name="cardName"
                    placeholder="John Doe"
                    value={formData.cardName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <FormInput
                    label="Expiry Date"
                    name="expiry"
                    placeholder="MM / YY"
                    value={formData.expiry}
                    onChange={handleInputChange}
                    required
                  />
                  <FormInput
                    label="Security Code"
                    name="cvv"
                    placeholder="CVV"
                    value={formData.cvv}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Place Order Button */}
            <button
              type="button"
              className={cn(
                "w-full flex items-center justify-center",
                "h-14 px-8",
                "bg-text-primary text-white",
                "text-sm font-medium uppercase tracking-wider",
                "hover:bg-primary-light transition-colors duration-300"
              )}
            >
              Complete Order - ${total}
            </button>

            {/* Terms */}
            <p className="text-xs text-text-muted text-center mt-6">
              By completing your order, you agree to our{' '}
              <button className="underline hover:text-text-primary transition-colors">Terms of Service</button>
              {' '}and{' '}
              <button className="underline hover:text-text-primary transition-colors">Privacy Policy</button>.
            </p>
          </motion.div>

          {/* Right: Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:border-l lg:border-border lg:pl-12"
          >
            <div className="sticky top-28">
              <h2 className="text-xs uppercase tracking-[0.2em] text-text-primary mb-6">
                Order Summary
              </h2>

              {/* Order Items */}
              <div className="mb-6">
                {orderItems.map((item) => (
                  <OrderSummaryItem key={item.id} item={item} />
                ))}
              </div>

              {/* Promo Code */}
              <div className="flex gap-3 mb-6">
                <input
                  type="text"
                  placeholder="Discount code"
                  className={cn(
                    "flex-1 h-12 px-4",
                    "bg-transparent border border-border",
                    "text-sm text-text-primary placeholder:text-text-muted",
                    "focus:outline-none focus:border-text-primary",
                    "transition-colors duration-200"
                  )}
                />
                <button
                  className={cn(
                    "px-6 h-12",
                    "bg-bg-tertiary text-text-secondary",
                    "text-sm uppercase tracking-wider",
                    "hover:bg-border hover:text-text-primary transition-colors duration-200"
                  )}
                >
                  Apply
                </button>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-border mb-6" />

              {/* Totals */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Subtotal</span>
                  <span className="text-text-primary">${subtotal}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Shipping</span>
                  <span className="text-text-primary">${shipping}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-secondary">Tax</span>
                  <span className="text-text-primary">${tax}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="w-full h-px bg-border my-6" />

              {/* Total */}
              <div className="flex justify-between items-center">
                <span className="text-sm uppercase tracking-wider text-text-primary">Total</span>
                <span className="text-2xl font-display text-text-primary">${total}</span>
              </div>

              {/* Trust Badges */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex flex-wrap gap-6 text-text-muted">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="text-xs">Secure Checkout</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                    </svg>
                    <span className="text-xs">Free Returns</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
